import Cookies from 'universal-cookie';

import { patchResidentInDrupal } from '../../../../utils/DrupalEntityTypes/Resident/ResidentHelpers';
import { getAuthClient } from '../../../../utils/auth';

const cookies = new Cookies();

const auth = getAuthClient();

/**
 * POST a message to one of the welcome-message AI endpoints, as the signed-in user.
 *
 * These endpoints invoke a model on every call, so they must be able to require
 * an authenticated caller. This is the admin-side Personalization form, so the
 * caller does hold a Drupal session — send the bearer token and the session
 * cookie with the request. The token is attached when there is one; an
 * anonymous caller still reaches the endpoint and gets whatever the server
 * decides to give it.
 *
 * @param {string} path
 * @param {string} message
 *
 * @returns {Promise<Response>}
 */
async function postMessageToAiEndpoint(path, message) {
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
    'X-Consumer-ID': process.env.REACT_APP_DRUPAL_CONSUMER_UUID,
  };

  try {
    const oauthToken = await auth.isLoggedIn();
    if (oauthToken?.access_token) {
      headers.Authorization = `Bearer ${oauthToken.access_token}`;
    }
  } catch (error) {
    // No usable token — fall through and let the endpoint decide.
  }

  return fetch(`${process.env.REACT_APP_YOUR_TOUR_CMS_URL}${path}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers,
    body: `message=${encodeURIComponent(message)}`,
  });
}

/**
 * Build the error for a failed AI endpoint response.
 *
 * A 4xx can carry the reason the endpoint refused the message — a 422 when it
 * discloses health information — and the counselor needs that reason to fix
 * the message. Use it when the body has one; otherwise keep the generic text.
 * An error built from the endpoint's reason is flagged so the form shows it
 * as-is rather than its own generic copy.
 *
 * @param {Response} response
 * @param {string} fallbackMessage
 *
 * @returns {Promise<Error>}
 */
async function buildEndpointError(response, fallbackMessage) {
  if (response.status >= 400 && response.status < 500) {
    try {
      const body = await response.json();
      if (typeof body?.error === 'string' && body.error.trim()) {
        const error = new Error(body.error);
        error.isEndpointReason = true;
        return error;
      }
    } catch (err) {
      // No JSON body — fall back to the generic message.
    }
  }
  return new Error(fallbackMessage);
}

/**
 * @returns {string|undefined}
 */
export function getSavedWelcomeMessage() {
  return cookies.get('welcome_message');
}

/**
 * @param {object} cookieDataDump
 * @param {string} conjunction
 *
 * @returns {string}
 */
export function getNames(cookieDataDump, conjunction = 'and') {
  if (cookieDataDump.person2_first_name) {
    return `${cookieDataDump.first_name} ${conjunction} ${cookieDataDump.person2_first_name}`;
  }
  return cookieDataDump.first_name;
}

/**
 * @param {string} message
 *
 * @returns {Promise<array>}
 */
export async function fetchWelcomeMessageVariations(message) {
  const response = await postMessageToAiEndpoint(
    '/your-tour-api/welcome-message-variations',
    message,
  );
  if (!response.ok) {
    throw await buildEndpointError(response, 'Failed to fetch welcome messages');
  }
  return response.json();
}

// Only a failure that plausibly never reached the model earns a second attempt:
// the request didn't get out, or a gateway in front of Drupal answered for it.
// A 500 or an unusable body can arrive after the model has already been billed
// for an answer, so retrying those would pay for it twice.
const RETRYABLE_STATUSES = [502, 503, 504];

/**
 * @param {string} message
 * @param {number} retryCount
 *
 * @returns {Promise<string>}
 */
export async function fetchRephrasedWelcomeMessage(message, retryCount = 0) {
  const canRetry = retryCount < 1;

  let response;
  try {
    response = await postMessageToAiEndpoint(
      '/your-tour-api/welcome-message-rewrite',
      message,
    );
  } catch (err) {
    if (canRetry) {
      return fetchRephrasedWelcomeMessage(message, retryCount + 1);
    }
    throw err;
  }

  if (!response.ok) {
    if (canRetry && RETRYABLE_STATUSES.includes(response.status)) {
      return fetchRephrasedWelcomeMessage(message, retryCount + 1);
    }
    throw await buildEndpointError(response, 'Failed to update welcome message');
  }

  const updatedMessage = await response.json();

  // The endpoint answers with a list of rewrites. Prefer the second so
  // rephrasing twice doesn't hand back the same wording, and fall back to the
  // first. Every entry is a { message } object — read that property rather
  // than the entry itself, or the caller's replace() throws.
  const rewritten = [updatedMessage?.[1], updatedMessage?.[0]]
    .find((item) => typeof item?.message === 'string' && item.message.trim());

  if (!rewritten) {
    throw new Error('Invalid response format from API');
  }

  return rewritten.message;
}

/**
 * @param {string} message
 * @param {string} residentUuid
 *
 * @returns {Promise<void>}
 */
export async function saveWelcomeMessageToCookie(message, residentUuid) {
  cookies.set('welcome_message', message, {
    path: '/',
    maxAge: 14400, // 4 hour cookie expiration.
    domain: window.location.hostname,
  });

  try {
    await patchResidentInDrupal(
      residentUuid,
      { field_favorites_page_message: message },
      null,
      {},
    );
  } catch (error) {
    console.error('Error updating resident data:', error);
    throw error;
  }
}
